import { useState } from 'react'
import { RefreshCw, Undo2 } from 'lucide-react'
import type { ModerationState } from '@/features/moderation/types'
import type { CourseWithoutBlocks } from '@/types/course'
import {
  courseFieldIssues,
  lessonFieldIssues,
  requiredLangs,
} from '@/features/editor/lib/publishIssues'
import { PublishIssuesTooltip } from '../EditorHeader/PublishIssuesTooltip'
import { useDesignOverflow } from '@/features/editor/lib/designOverflow'
import { useHasStartedTranslationRun } from '../studio/useBlocksInRun'
import { useHasUnsavedBlockDraft } from '../studio/aiSidebar/useUnsavedBlockGate'
import { useBlockDraftContext } from '../studio/BlockDraftContext'
import { DraftChangesDialogs } from './DraftChangesDialogs'
import { ChangesFrame } from './ChangesFrame'
import { BusyButton } from './BusyButton'
import type { useCourseUpdate } from './useCourseUpdate'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  /** Курс редактора — по нему список того, что мешает обновлению. */
  course: CourseWithoutBlocks
  /** Владелец — `PublishDialogBody`: его `isBusy` гасит и смену доступа. */
  update: ReturnType<typeof useCourseUpdate>
  /** Монтируется только при `draftId !== null` — ручки черновика без него бессмысленны. */
  moderation: ModerationState
  isCourseAuthor: boolean
  /** Смена доступа в полёте — `PATCH /visibility` и `POST submit-update` не идут параллельно. */
  accessBusy: boolean
  /** Отсчёт `429` смены доступа: антиспам проверки общий, заявка упрётся в тот же отказ. */
  accessRetryLabel: string | null
  /** Только кнопки, без своей карточки и заголовка: после отказа проверки они
   *  стоят прямо в карточке отказа (`ReviewStatusPanel.actions`). */
  bare?: boolean
}

/**
 * Секция «Изменения» окна публикации: черновик курса (контракт §3a).
 * У живого курса — «Отправить обновление»; у снятого обновление уйдёт вместе с
 * публикацией селектом доступа, поэтому там только строка об этом и «Вернуть опубликованную версию».
 */
export const DraftChangesSection = ({
  course,
  update,
  moderation,
  isCourseAuthor,
  accessBusy,
  accessRetryLabel,
  bare,
}: Props) => {
  const m = useMessages(messages)
  const [discardOpen, setDiscardOpen] = useState(false)
  // Какое действие запущено — индикатор крутится на той кнопке, которую нажали,
  // сразу на клике (занятость `update.isBusy` ставится синхронно), а не после
  // ответа сервера.
  const [action, setAction] = useState<'update' | 'discard' | null>(null)
  const busyFor = (a: 'update' | 'discard'): boolean => update.isBusy && action === a
  // Несохранённый экран в обновление не попадёт: ученики получили бы не ту версию.
  const hasUnsavedBlock = useHasUnsavedBlockDraft()
  const { doSave } = useBlockDraftContext()
  // Гейт замеров (`issuesBlocked`) меряет СОХРАНЁННЫЕ блоки, а холст показывает
  // правку, которая до сервера ещё не дошла (автосохранение через 700 мс): клик
  // дописал бы красный блок и сразу отправил его. Пока правка не сохранена,
  // судим по живому уровню холста.
  const liveOverflow = useDesignOverflow()
  const unsavedOverflow = hasUnsavedBlock && liveOverflow === 'over'
  // Незаполненное (поля курса и уроков, экраны: пустое видео, квиз без ответа)
  // гасит «Обновить», а список с «Перейти» — облачком по наведению на неё.
  const startedRun = useHasStartedTranslationRun(course.id)
  const [issuesBlocked, setIssuesBlocked] = useState(false)
  const live = moderation.visibility !== 'unpublished'
  // Без несданных правок (заявка висит, черновик совпал или пуст) предлагать нечего —
  // и у живого курса, и у снятого.
  if (!moderation.hasUnsubmittedChanges) return null
  const pending = moderation.review?.status === 'pending'
  const locked = update.isBusy || accessBusy || pending || !isCourseAuthor
  // Под отсчётом смены доступа её строка уже стоит под селектом — здесь только гасим кнопку.
  const retryLabel = update.retryLabel ?? accessRetryLabel

  return (
    <ChangesFrame bare={bare} live={live}>
      {/* Две кнопки одной ширины одна под другой: «Обновить» — главная (бренд),
          откат к опубликованной — второстепенная (серая), со своим значком. */}
      <div className="flex flex-col gap-2">
        {/* «Обновить» есть и у закрытого курса с черновиком правок (решение
            заказчика): правки уходят на проверку тем же запросом, а замечания
            черновика видны по наведению на эту кнопку, а не на кнопку шапки. */}
        <PublishIssuesTooltip
          eager
          className="flex w-full"
          lessons={course.lessons ?? []}
          fieldIssues={courseFieldIssues(course, startedRun)}
          lessonIssues={lessonFieldIssues(course, startedRun)}
          langs={requiredLangs(course, startedRun)}
          onBlocked={setIssuesBlocked}
        >
          <BusyButton
            className="w-full"
            busy={busyFor('update')}
            icon={RefreshCw}
            // Прошлая заявка отклонена проверкой — кнопка говорит, что автор
            // исправил нарушения (решение заказчика); иначе — обычное «Обновить».
            label={moderation.review?.status === 'rejected' ? m.sendUpdateFixed : m.sendUpdate}
            busyLabel={m.updating}
            disabled={locked || retryLabel !== null || issuesBlocked || unsavedOverflow}
            onClick={() => {
              if (unsavedOverflow) return
              setAction('update')
              // Несохранённая правка блока сперва дописывается, потом уходит
              // обновление: блоки сохраняются сами, а отдельный запрет «Сначала
              // сохраните блок» мигал на каждом автосохранении.
              void (hasUnsavedBlock ? doSave() : Promise.resolve()).then(() =>
                update.requestSubmit(),
              )
            }}
          />
        </PublishIssuesTooltip>
        <BusyButton
          variant="secondary"
          // Своя заливка: штатная `secondary` сливалась с фоном секции.
          className="w-full bg-foreground/10 text-foreground hover:bg-foreground/[0.18]"
          busy={busyFor('discard')}
          icon={Undo2}
          label={m.discardChanges}
          busyLabel={m.discarding}
          disabled={locked}
          onClick={() => setDiscardOpen(true)}
        />
      </div>
      {update.retryLabel ? (
        <p className="text-xs text-muted-foreground">{m.retryIn(update.retryLabel)}</p>
      ) : null}

      <DraftChangesDialogs
        update={update}
        discardOpen={discardOpen}
        onDiscardOpenChange={setDiscardOpen}
        onDiscard={() => {
          setAction('discard')
          void update.discard()
        }}
      />
    </ChangesFrame>
  )
}
